import { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography
} from "@material-ui/core";
import { AssignmentTurnedIn } from "@material-ui/icons";
import { api } from "../../services/apiService";
import "./absite-program-code-request.scss";

const initialForm = {
  programName: "",
  institutionName: "",
  requestorName: "",
  requestorEmail: "",
  desiredProgramCode: "",
  notes: ""
};

const requiredFields = [
  "programName",
  "institutionName",
  "requestorName",
  "requestorEmail"
];

const hasWhitespace = (value) => Array.from(value).some((character) => character.trim() === "");

const isValidEmail = (email) => {
  const value = email.trim();
  const atIndex = value.indexOf("@");
  const lastDotIndex = value.lastIndexOf(".");

  return atIndex > 0
    && atIndex === value.lastIndexOf("@")
    && lastDotIndex > atIndex + 1
    && lastDotIndex < value.length - 1
    && !hasWhitespace(value);
};

const extractErrorMessage = (response) => {
  if (response?.errMsg?.message) {
    return response.errMsg.message;
  }
  if (typeof response?.errMsg === "string") {
    return response.errMsg;
  }
  return "Could not request a program code. Please try again.";
};

export default function AbsiteProgramCodeRequestPage() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [programCode, setProgramCode] = useState(null);
  const hasEmailError = form.requestorEmail.trim() && !isValidEmail(form.requestorEmail);

  const canSubmit = useMemo(
    () => requiredFields.every((field) => form[field].trim())
      && isValidEmail(form.requestorEmail)
      && !loading,
    [form, loading]
  );

  const diagnosticLink = programCode?.programCode
    ? `/absite-diagnostic?programCode=${encodeURIComponent(programCode.programCode)}`
    : "";

  const onFieldChange = (field) => (event) => {
    setForm({
      ...form,
      [field]: event.target.value
    });
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }

    setLoading(true);
    setError("");
    setProgramCode(null);
    try {
      const response = await api.requestAbsiteProgramCode({
        programName: form.programName.trim(),
        institutionName: form.institutionName.trim(),
        requestorName: form.requestorName.trim(),
        requestorEmail: form.requestorEmail.trim(),
        desiredProgramCode: form.desiredProgramCode.trim(),
        notes: form.notes.trim()
      });

      if (response?.errStatus) {
        setError(extractErrorMessage(response));
        return;
      }

      setProgramCode(response);
    } catch {
      setError("Could not request a program code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="div" className="program-code-request-page">
      <Box component="main" className="program-code-request-shell">
        <Box component="section" className="program-code-request-header">
          <Box component="div" className="program-code-request-eyebrow">
            ABSITE pilots
          </Box>
          <Typography component="h1" className="program-code-request-title">
            Request a program code
          </Typography>
          <Typography component="p" className="program-code-request-copy">
            Create a pending code for your residency program diagnostic.
          </Typography>
        </Box>

        <Paper elevation={0} className="program-code-request-panel">
          <form noValidate onSubmit={onSubmit} className="program-code-request-form">
            <TextField
              id="program-code-request-program-name"
              label="Program name"
              value={form.programName}
              onChange={onFieldChange("programName")}
              variant="outlined"
              required
              fullWidth
            />
            <TextField
              id="program-code-request-institution-name"
              label="Institution name"
              value={form.institutionName}
              onChange={onFieldChange("institutionName")}
              variant="outlined"
              required
              fullWidth
            />
            <TextField
              id="program-code-request-requestor-name"
              label="Requestor name"
              value={form.requestorName}
              onChange={onFieldChange("requestorName")}
              variant="outlined"
              required
              fullWidth
            />
            <TextField
              id="program-code-request-requestor-email"
              label="Requestor email"
              value={form.requestorEmail}
              onChange={onFieldChange("requestorEmail")}
              variant="outlined"
              type="email"
              error={Boolean(hasEmailError)}
              helperText={hasEmailError ? "Enter a valid email address." : ""}
              required
              fullWidth
            />
            <TextField
              id="program-code-request-desired-code"
              label="Desired code"
              value={form.desiredProgramCode}
              onChange={onFieldChange("desiredProgramCode")}
              variant="outlined"
              fullWidth
            />
            <TextField
              id="program-code-request-notes"
              label="Notes"
              value={form.notes}
              onChange={onFieldChange("notes")}
              variant="outlined"
              multiline
              minRows={3}
              fullWidth
            />

            {error && (
              <Box
                component="div"
                className="program-code-request-error"
                role="alert"
              >
                {error}
              </Box>
            )}

            <Button
              color="primary"
              variant="contained"
              type="submit"
              className="program-code-request-submit"
              disabled={!canSubmit}
              startIcon={loading ? <CircularProgress size={18} /> : <AssignmentTurnedIn />}
            >
              Request code
            </Button>
          </form>
        </Paper>

        {programCode && (
          <Paper elevation={0} className="program-code-request-success">
            <Typography component="h2">Program code</Typography>
            <Box component="div" className="program-code-request-code">
              {programCode.programCode}
            </Box>
            <Box component="div" className="program-code-request-status">
              {programCode.status}
            </Box>
            <Button
              color="primary"
              variant="outlined"
              href={diagnosticLink}
              className="program-code-request-link"
            >
              Open diagnostic link
            </Button>
          </Paper>
        )}

        <Typography component="p" className="program-code-request-disclaimer">
          QLearn is not affiliated with or endorsed by the American Board of Surgery.
        </Typography>
      </Box>
    </Box>
  );
}
